/**
 * Shared category/vouch constants for the bank transactions panels.
 *
 * Split out of `badges.tsx` (react-refresh/only-export-components) — both the
 * "Documents" view and the "All Transactions" view key off these constants so
 * category/vouch labels + colors stay identical across the two panels.
 */

import type { BadgeColor } from "@/ui/primitives/badge";

export const TYPE_LABELS: Record<string, string> = {
  employee: "Possible Employee",
  trade_third_parties: "Trade & Third Parties",
  government_statutory: "Government & Statutory",
  financial_institutions: "Financial Institutions",
  utilities_rent: "Utilities & Rent",
  internal_intercompany: "Internal/Inter-company",
  professional_advisors: "Professional Advisors",
  software_subscriptions: "Software & Subscriptions",
  insurance: "Insurance",
  fuel: "Fuel",
  retail: "Retail",
  travel: "Travel",
  related_party: "Related Party",
  unknown: "Unknown",
};

export const TYPE_BADGE_COLOR: Record<string, BadgeColor> = {
  employee: "purple",
  trade_third_parties: "blue",
  government_statutory: "teal",
  financial_institutions: "indigo",
  utilities_rent: "sky",
  internal_intercompany: "orange",
  professional_advisors: "pink",
  software_subscriptions: "blue",
  insurance: "teal",
  fuel: "orange",
  retail: "indigo",
  travel: "emerald",
  related_party: "red",
  unknown: "gray",
};

// ---------------------------------------------------------------------------
// Vouching (MOR-574) — bank ↔ ledger (Xero/GL) match status
// ---------------------------------------------------------------------------

export type VouchStatusKey = "vouched" | "bank_only" | "ledger_only";

export const VOUCH_CONFIG: Record<
  VouchStatusKey,
  { label: string; color: BadgeColor; tooltip: string }
> = {
  vouched: {
    label: "Vouched",
    color: "emerald",
    tooltip:
      "Matched in both the bank statement and the ledger — confirmed cash movement.",
  },
  bank_only: {
    label: "Bank only",
    color: "amber",
    tooltip:
      "In the bank statement but not the ledger — real movement missing from the accounting records.",
  },
  ledger_only: {
    label: "Ledger only",
    color: "amber",
    tooltip:
      "In the ledger but not the bank — candidate disregard / adjustment-back (never hit the bank).",
  },
};
